import {Injectable} from '@angular/core';
import {AuthService} from '../../authentication/auth-service';
import {environment} from '../../environments/environment';
import {ApplicationConfiguration} from '../../environments/application-configurations';
import {CreateFormRequest, FormDetail, FormSummary} from '../entity/admin-form.model';

@Injectable({providedIn: 'root'})
export class AdminFormService {
  private readonly env: ApplicationConfiguration = environment;

  constructor(private readonly authService: AuthService) {}

  async listForms(): Promise<FormSummary[]> {
    const response = await fetch(`${this.env.claimManagerBackend.baseUrl}/api/forms`, {
      headers: this.getAuthHeaders()
    });
    if (!response.ok) throw new Error(response.statusText);
    return response.json();
  }

  async getForm(formId: string, version: string): Promise<FormDetail> {
    const response = await fetch(
      `${this.env.claimManagerBackend.baseUrl}/api/public/form/${formId}/${version}`,
      {headers: this.getAuthHeaders()}
    );
    if (!response.ok) throw new Error(response.statusText);
    return response.json();
  }

  async saveForm(form: CreateFormRequest): Promise<void> {
    const response = await fetch(`${this.env.claimManagerBackend.baseUrl}/api/forms`, {
      method: 'POST',
      headers: {...this.getAuthHeaders(), 'Content-Type': 'application/json'},
      body: JSON.stringify(form)
    });
    if (!response.ok) throw new Error(response.statusText);
  }

  async publishForm(formId: string, version: string): Promise<void> {
    const response = await fetch(
      `${this.env.claimManagerBackend.baseUrl}/api/forms/${formId}/${version}/publish`,
      {
        method: 'POST',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || response.statusText);
    }
  }

  async dryRunForm(formId: string, version: string): Promise<void> {
    const response = await fetch(
      `${this.env.claimManagerBackend.baseUrl}/api/forms/${formId}/${version}/dryrun`,
      {
        method: 'POST',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || response.statusText);
    }
  }

  async unpublishForm(formId: string, version: string): Promise<void> {
    const response = await fetch(
      `${this.env.claimManagerBackend.baseUrl}/api/forms/${formId}/${version}/unpublish`,
      {
        method: 'POST',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || response.statusText);
    }
  }

  async deleteForm(formId: string, version: string): Promise<void> {
    const response = await fetch(
      `${this.env.claimManagerBackend.baseUrl}/api/forms/${formId}/${version}/delete`,
      {
        method: 'POST',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || response.statusText);
    }
  }

  async getLogoUploadUrl(formId: string, version: string, extension: string): Promise<{ presignedUrl: string; s3Key: string }> {
    const response = await fetch(
      `${this.env.claimManagerBackend.baseUrl}/api/forms/${formId}/${version}/logo-upload-url?extension=${encodeURIComponent(extension)}`,
      {
        method: 'POST',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || response.statusText);
    }
    return response.json();
  }

  async deleteLogo(formId: string, version: string): Promise<void> {
    const response = await fetch(
      `${this.env.claimManagerBackend.baseUrl}/api/forms/${formId}/${version}/logo-delete`,
      {
        method: 'POST',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || response.statusText);
    }
  }

  async uploadLogoToS3(presignedUrl: string, file: File): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
    if (!response.ok) throw new Error(response.statusText);
  }

  async getDataSourceUploadUrl(formId: string, version: string, sourceId: string): Promise<{ presignedUrl: string; dataSourceS3Key: string; frontendFileS3Key: string }> {
    const response = await fetch(
      `${this.env.claimManagerBackend.baseUrl}/api/forms/${formId}/${version}/autocomplete-sources/${encodeURIComponent(sourceId)}/upload-url`,
      {
        method: 'POST',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || response.statusText);
    }
    return response.json();
  }

  async uploadDataSourceToS3(presignedUrl: string, file: File): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) throw new Error(response.statusText);
  }

  async processDataSource(formId: string, version: string, sourceId: string): Promise<{ entryCount: number }> {
    const response = await fetch(
      `${this.env.claimManagerBackend.baseUrl}/api/forms/${formId}/${version}/autocomplete-sources/${encodeURIComponent(sourceId)}/process`,
      {
        method: 'POST',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || response.statusText);
    }
    return response.json();
  }



  private getAuthHeaders(): Record<string, string> {
    const token = this.authService.getIdToken();
    return token ? {'Authorization': `Bearer ${token}`} : {};
  }
}
